import React from "react";
import { Stamp, Fingerprint, Hash, Clock, ShieldCheck, KeyRound, Link as LinkIcon, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const Row = ({ icon: Icon, label, value, mono = true, testId }) => (
    <div data-testid={testId} className="flex items-start gap-3 py-3 border-b border-dashed border-slate-300/80 last:border-b-0">
        <Icon className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {label}
            </div>
            <div
                className={`mt-1 ${mono ? "font-mono text-xs" : "text-sm"} text-slate-900 break-all`}
            >
                {value ?? <span className="text-slate-400">—</span>}
            </div>
        </div>
    </div>
);

export const ReceiptPanel = ({ receipt }) => {
    if (!receipt) {
        return (
            <div
                data-testid="signed-receipt-empty"
                className="border border-dashed border-slate-300 rounded-sm p-10 receipt-paper"
            >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Step 5 · Awaiting authority gate
                </div>
                <p className="mt-3 font-heading text-2xl text-slate-700 max-w-xl">
                    A KATLAS-signed receipt will appear here once a governed action is taken.
                </p>
            </div>
        );
    }

    const verificationOk = receipt.verification === "SUCCESS";

    return (
        <div
            data-testid="signed-receipt-panel"
            className="stamp-in border border-slate-300 bg-white rounded-sm receipt-paper relative overflow-hidden"
        >
            <div className="absolute -right-6 -top-6 opacity-[0.06] pointer-events-none">
                <Stamp className="h-44 w-44 text-slate-900" />
            </div>

            <div className="px-8 pt-8 pb-6 border-b border-slate-300">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            KATLAS signed receipt
                        </div>
                        <div className="mt-2 font-heading text-2xl lg:text-3xl font-semibold text-slate-900">
                            {receipt.action_label}
                        </div>
                    </div>
                    <div
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] ${
                            verificationOk
                                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                : "bg-amber-50 border-amber-200 text-amber-800"
                        }`}
                        data-testid="receipt-verification"
                    >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Verification: {receipt.verification || "—"}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 px-8 py-6">
                <div>
                    <Row icon={Hash} label="Receipt hash" value={receipt.receipt_hash} testId="receipt-hash" />
                    <Row icon={LinkIcon} label="Attestation reference" value={receipt.attestation_reference} testId="receipt-attestation" />
                    <Row icon={KeyRound} label="Signer issuer" value={receipt.signer?.issuer} testId="receipt-signer-issuer" />
                    <Row icon={KeyRound} label="Signer key id" value={receipt.signer?.key_id} testId="receipt-signer-keyid" />
                    <Row icon={Fingerprint} label="Signer fingerprint" value={receipt.signer?.fingerprint} testId="receipt-signer-fpr" />
                </div>
                <div>
                    <Row icon={Clock} label="KATLAS node timestamp" value={receipt.node_timestamp_utc} testId="receipt-node-ts" />
                    <Row icon={Clock} label="Local timestamp" value={receipt.local_timestamp_utc} testId="receipt-local-ts" />
                    <Row icon={ShieldCheck} label="Model route chosen" value={receipt.model_route_chosen} mono={false} testId="receipt-model-route" />
                    <Row
                        icon={ShieldCheck}
                        label={`Shared evidence refs · ${receipt.shared_evidence_refs?.length ?? 0}`}
                        value={receipt.shared_evidence_refs?.join("\n")}
                        testId="receipt-shared-refs"
                    />
                    <Row
                        icon={ShieldCheck}
                        label="Withheld evidence count"
                        value={receipt.withheld_evidence_count}
                        testId="receipt-withheld-count"
                    />
                </div>
            </div>

            <div className="px-8 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">
                    Cost rationale
                </div>
                <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
                    {receipt.cost_rationale_summary}
                </p>
            </div>

            <div className="mx-8 mb-8 border border-dashed border-slate-300 p-6 rounded-sm bg-slate-50/50">
                <div data-testid="car-block" className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <CarBlock title="Custody" body={receipt.car_summary?.custody} />
                    <CarBlock title="Authority" body={receipt.car_summary?.authority} />
                    <CarBlock title="Receipts" body={receipt.car_summary?.receipts} />
                </div>
                <p className="mt-6 text-xs text-slate-500 italic max-w-3xl">
                    {receipt.data_boundary_statement}
                </p>
            </div>

            {receipt.receipt_hash && (
                <div className="px-8 pb-8">
                    <Link
                        to={`/r/${encodeURIComponent(receipt.receipt_hash)}`}
                        data-testid="receipt-public-link"
                        className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 hover:bg-slate-50 hover-lift"
                    >
                        Open public-safe receipt
                        <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                </div>
            )}
        </div>
    );
};

const CarBlock = ({ title, body }) => (
    <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            {title}
        </div>
        <p className="mt-2 text-sm text-slate-800 leading-relaxed">{body}</p>
    </div>
);

export default ReceiptPanel;
